<template>
  <div class="page-container animate-fade-in">
    <PageHeader
      title="Profile & Home"
      description="Edit hero content, about details, and social links."
      :breadcrumbs="[{ label: 'Admin', to: '/admin' }, { label: 'Profile' }]"
    />

    <form class="space-y-6" @submit.prevent="save">
      <BaseCard title="Hero">
        <div class="grid sm:grid-cols-2 gap-5 mt-2">
          <BaseInput v-model="form.name" label="Display name" />
          <BaseInput v-model="form.fullName" label="Full name" />
          <BaseInput v-model="form.title" label="Job title" />
          <BaseInput v-model="form.bio" label="Short bio" />
          <BaseTextarea v-model="form.moto" class="sm:col-span-2" label="Motto" :rows="2" />
          <BaseTextarea v-model="form.verse" class="sm:col-span-2" label="Verse / quote" :rows="2" />
        </div>
        <div class="mt-6">
          <p class="text-label mb-2">Rotating roles (home page)</p>
          <AdminStringList v-model="form.roles" placeholder="e.g. Full Stack Developer" add-label="Add role" />
        </div>
      </BaseCard>

      <BaseCard title="Home stats">
        <div class="grid sm:grid-cols-2 gap-4 mt-2">
          <div v-for="(stat, index) in form.homeStats" :key="index" class="p-4 rounded-xl glass-subtle grid sm:grid-cols-2 gap-3">
            <BaseInput v-model="stat.value" label="Value" />
            <BaseInput v-model="stat.label" label="Label" />
          </div>
        </div>
      </BaseCard>

      <BaseCard title="About details">
        <div class="grid sm:grid-cols-2 gap-5 mt-2">
          <BaseInput v-model="form.informations.birthday" label="Birthday" />
          <BaseInput v-model="form.informations.degree" label="Degree" />
          <BaseInput v-model="form.informations.email" label="Email" type="email" />
          <BaseInput v-model="form.informations.contact" label="Phone" />
          <BaseInput v-model="form.informations.Website" label="Website" class="sm:col-span-2" />
          <BaseInput v-model="form.informations.homeaddress" label="Address" class="sm:col-span-2" />
          <label class="flex items-center gap-2 text-sm sm:col-span-2">
            <input v-model="form.informations.freelance" type="checkbox" class="rounded border-border text-primary focus:ring-primary" />
            Available for freelance
          </label>
        </div>
      </BaseCard>

      <BaseCard title="Social links">
        <div class="grid sm:grid-cols-2 gap-5 mt-2">
          <BaseInput v-model="form.social.facebook" label="Facebook URL" />
          <BaseInput v-model="form.social.linkedin" label="LinkedIn URL" />
          <BaseInput v-model="form.social.instagram" label="Instagram URL" class="sm:col-span-2" />
        </div>
      </BaseCard>

      <div class="flex justify-end gap-3">
        <BaseButton variant="secondary" type="button" tag="router-link" to="/admin">Cancel</BaseButton>
        <BaseButton variant="primary" type="submit" :loading="saving">Save changes</BaseButton>
      </div>
    </form>
  </div>
</template>

<script>
import { siteState, saveSiteData } from '@/composables/useSiteData'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseTextarea from '@/components/ui/BaseTextarea.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AdminStringList from '@/components/admin/AdminStringList.vue'

export default {
  name: 'AdminProfile',
  components: { PageHeader, BaseCard, BaseInput, BaseTextarea, BaseButton, AdminStringList },
  data() {
    return {
      saving: false,
      form: {
        name: '',
        fullName: '',
        title: '',
        bio: '',
        moto: '',
        verse: '',
        roles: [],
        informations: {},
        social: {},
        homeStats: [],
      },
    }
  },
  created() {
    this.loadForm()
  },
  methods: {
    loadForm() {
      this.form = {
        ...JSON.parse(JSON.stringify(siteState.profile)),
        roles: [...siteState.profile.roles],
        informations: JSON.parse(JSON.stringify(siteState.profile.informations)),
        social: JSON.parse(JSON.stringify(siteState.profile.social)),
        homeStats: JSON.parse(JSON.stringify(siteState.homeStats)),
      }
    },
    async save() {
      this.saving = true
      const { success, error } = useToast()
      try {
        const profile = {
          name: this.form.name,
          fullName: this.form.fullName,
          title: this.form.title,
          bio: this.form.bio,
          moto: this.form.moto,
          verse: this.form.verse,
          roles: this.form.roles.filter(Boolean),
          informations: this.form.informations,
          social: this.form.social,
        }
        profile.informations.name = this.form.fullName
        await saveSiteData({ profile, homeStats: this.form.homeStats })
        success('Profile saved', 'Your profile and home content were updated.')
      } catch (e) {
        error('Save failed', 'Could not save profile data.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
